import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createRally,
  createRallyPricings,
  deleteRally,
  getActiveRally,
  getAllRallies,
  getRallyById,
  getRallyCompetitors,
  getRallyStages,
  updateRally,
  updateRallyBanner,
  updateRallyThumbnail,
} from "./rally.service";

export const useGetRallies = (params = {}, options = {}) => {
  const status = params?.status || "";
  const type = params?.type || "";

  return useQuery({
    queryKey: ["rallies", status, type],
    queryFn: () => getAllRallies(params),
    ...options,
  });
};

export const useGetActiveRally = (options = {}) => {
  return useQuery({
    queryKey: ["rallies", "active"],
    queryFn: getActiveRally,
    ...options,
  });
};

export const useGetRallyById = (rallyId) => {
  return useQuery({
    queryKey: ["rallies", rallyId],
    queryFn: () => getRallyById(rallyId),
    enabled: Boolean(rallyId),
  });
};

export const useGetRallyStages = (eventId, options = {}) => {
  return useQuery({
    queryKey: ["rallies", eventId, "stages"],
    queryFn: () => getRallyStages(eventId),
    enabled: Boolean(eventId) && options.enabled !== false,
    ...options,
  });
};

export const useGetRallyCompetitors = (eventId, params = {}, options = {}) => {
  const category = params?.category || "";
  return useQuery({
    queryKey: ["rallies", eventId, "competitors", category],
    queryFn: () =>
      getRallyCompetitors(eventId, category ? { category } : {}),
    enabled: Boolean(eventId) && options.enabled !== false,
    ...options,
  });
};

export const useCreateRallyMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRally,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rallies"] });
    },
  });
};

export const useUpdateRallyMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ rallyId, rallyData }) => updateRally(rallyId, rallyData),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["rallies"] });
      if (variables?.rallyId) {
        queryClient.invalidateQueries({ queryKey: ["rallies", variables.rallyId] });
      }
    },
  });
};

export const useDeleteRallyMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteRally,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rallies"] });
    },
  });
};

export const useUpdateRallyBannerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ rallyId, formData }) => updateRallyBanner(rallyId, formData),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["rallies"] });
      if (variables?.rallyId) {
        queryClient.invalidateQueries({
          queryKey: ["rallies", variables.rallyId],
        });
      }
    },
  });
};

export const useUpdateRallyThumbnailMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ rallyId, formData }) =>
      updateRallyThumbnail(rallyId, formData),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["rallies"] });
      if (variables?.rallyId) {
        queryClient.invalidateQueries({
          queryKey: ["rallies", variables.rallyId],
        });
      }
    },
  });
};

/** @deprecated Prefer sending `prices` on create/update rally payload. */
export const useCreateRallyPricingsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, prices }) => createRallyPricings(eventId, prices),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["rallies"] });
      if (variables?.eventId) {
        queryClient.invalidateQueries({
          queryKey: ["rallies", variables.eventId],
        });
      }
    },
  });
};
